// Which checkout provider a pipe renders. Shared by the `Checkout` component, the
// builder's pre-warm gate (builder/lib/pages.ts getActiveIntegrations) and GTM's
// `checkoutProvider` tag, so the three can never disagree. Dependency-free on
// purpose: the component imports it without pulling in the integration registry,
// and the co-located test runs under bare `node --test`.

// One pipe may hold both checkouts, each behind a visibility rule on this constant, with the
// value overridden per distribution. The build then activates only the selected provider's
// integration (builder/lib/pages.ts getActiveIntegrations) and GTM tags with the same value.
// Absent or unrecognised → no gating, so single-provider pipes are unaffected.
export const CHECKOUT_VERSION_CONSTANT = 'global.checkoutVersion'

export type CheckoutProvider = 'primer' | 'yuno'

export const normalizeCheckoutProvider = (value: unknown): CheckoutProvider | undefined => {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return v === 'primer' || v === 'yuno' ? v : undefined
}

/** Registry key of the provider-agnostic checkout (`ui/src/render/index.ts`). */
export const CHECKOUT_COMPONENT_TYPE = 'Checkout'

/**
 * What a `Checkout` renders when the constant is absent or unrecognised. Unlike
 * the legacy PrimerCheckout/YunoCheckout pair — whose rules hide BOTH checkouts
 * in that case — a `Checkout` always renders one.
 */
export const FALLBACK_CHECKOUT_PROVIDER: CheckoutProvider = 'yuno'

/**
 * The provider every `Checkout` node renders: the distribution-resolved
 * constant, otherwise the fallback. There is deliberately no per-node choice —
 * the distribution alone decides, so switching provider is one override.
 * Case-insensitive — the rule-based pair compared the constant case-sensitively.
 */
export const resolveCheckoutProvider = (constantValue: unknown): CheckoutProvider =>
  normalizeCheckoutProvider(constantValue) ?? FALLBACK_CHECKOUT_PROVIDER
